import React, { useEffect, useState, useRef } from 'react';
import { Mail, AlertTriangle, X, ChevronRight } from 'lucide-react';
import adminApi from '../lib/adminApi';

/**
 * EmailQuotaBanner — rolling 24h / 1h Gmail-send usage for HED's shared
 * mailbox. Renders only for super admins; polls every 60s and lights up
 * green → amber → red as usage climbs toward Gmail's real ceilings
 * (2000/24h, 500/1h).
 *
 * Backend source of truth: /api/admin/email/quota which reads the
 * `email_sends` collection we write in shared/mail.py after every
 * confirmed Gmail response.
 */
export default function EmailQuotaBanner() {
  const [quota, setQuota] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [dismissedAt, setDismissedAt] = useState(0);
  const [recent, setRecent] = useState(null);
  const timerRef = useRef(null);

  const fetchQuota = async () => {
    try {
      const { data } = await adminApi.get('/api/admin/email/quota');
      setQuota(data);
    } catch {
      // Silent — the banner is nice-to-have; a network blip shouldn't nag the admin
    }
  };

  useEffect(() => {
    fetchQuota();
    timerRef.current = setInterval(fetchQuota, 60_000);
    return () => clearInterval(timerRef.current);
  }, []);

  if (!quota || !quota.is_super_admin) return null;
  // Never nag when we're comfortably in the green
  if (quota.status === 'green' && !expanded) return null;
  // Respect a dismiss for 15 minutes
  if (Date.now() - dismissedAt < 15 * 60 * 1000 && quota.status !== 'red') return null;

  const palette = {
    green: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-800', bar: 'bg-emerald-500' },
    amber: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800', bar: 'bg-amber-500' },
    red: { bg: 'bg-red-50', border: 'border-red-300', text: 'text-red-800', bar: 'bg-red-500' },
  }[quota.status];

  const share24 = Math.min(100, Math.round((quota.used_24h / quota.cap_24h) * 100));
  const share1 = Math.min(100, Math.round((quota.used_1h / quota.cap_1h) * 100));

  const loadRecent = async () => {
    if (recent) return;
    try {
      const { data } = await adminApi.get('/api/admin/email/recent-sends?limit=15');
      setRecent(data || []);
    } catch { /* ignored */ }
  };

  return (
    <div className={`${palette.bg} ${palette.text} border-b ${palette.border}`} data-testid="email-quota-banner">
      <div className="max-w-[1600px] mx-auto px-4 py-2 flex items-center gap-3 text-sm">
        {quota.status === 'red' ? <AlertTriangle className="w-4 h-4 shrink-0" /> : <Mail className="w-4 h-4 shrink-0" />}
        <div className="flex-1 flex flex-wrap items-center gap-x-4 gap-y-1">
          <span className="font-medium">
            {quota.status === 'red' ? 'Email sending is close to Gmail\'s daily limit' :
             quota.status === 'amber' ? 'Email sending is approaching Gmail\'s daily limit' :
             'Email usage'}
          </span>
          <span className="tabular-nums">
            24h: <b>{quota.used_24h.toLocaleString('en-IN')}</b> / {quota.cap_24h.toLocaleString('en-IN')}
            <span className="mx-1">·</span>
            1h: <b>{quota.used_1h.toLocaleString('en-IN')}</b> / {quota.cap_1h.toLocaleString('en-IN')}
          </span>
          <div className="hidden md:flex items-center gap-1 min-w-[140px]">
            <div className="h-1.5 rounded-full bg-white/60 flex-1 overflow-hidden">
              <div className={`${palette.bar} h-full`} style={{ width: `${share24}%` }} />
            </div>
            <span className="text-xs">{share24}%</span>
          </div>
          <button
            className="text-xs underline underline-offset-2 hover:opacity-80"
            onClick={() => { setExpanded(v => !v); loadRecent(); }}
            data-testid="email-quota-details-btn"
          >
            {expanded ? 'Hide details' : 'Show recent sends'}
            <ChevronRight className={`w-3 h-3 inline transition-transform ${expanded ? 'rotate-90' : ''}`} />
          </button>
        </div>
        <button onClick={() => setDismissedAt(Date.now())} title="Snooze 15 min" className="p-1 hover:bg-white/40 rounded" data-testid="email-quota-dismiss">
          <X className="w-4 h-4" />
        </button>
      </div>
      {expanded && recent && (
        <div className="max-w-[1600px] mx-auto px-4 pb-3">
          <div className="bg-white/60 rounded-lg border border-white/60 overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-white/40 text-neutral-600">
                <tr>
                  <th className="px-3 py-1.5 text-left font-medium">Time</th>
                  <th className="px-3 py-1.5 text-left font-medium">From</th>
                  <th className="px-3 py-1.5 text-left font-medium">To</th>
                  <th className="px-3 py-1.5 text-left font-medium">Subject</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/50">
                {recent.length === 0 && (
                  <tr><td colSpan={4} className="px-3 py-3 text-center text-neutral-500">No emails sent in this window.</td></tr>
                )}
                {recent.map(r => (
                  <tr key={r._id} className="hover:bg-white/70">
                    <td className="px-3 py-1.5 tabular-nums text-neutral-600">{r.sent_at?.replace('T', ' ').slice(0, 16)}</td>
                    <td className="px-3 py-1.5 text-neutral-800">{r.sender}</td>
                    <td className="px-3 py-1.5 text-neutral-800">{(r.to || []).join(', ')}</td>
                    <td className="px-3 py-1.5 text-neutral-600 truncate max-w-[280px]">{r.subject}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[10px] text-neutral-500 mt-2">
            Counters reset on a 24-hour rolling window aligned to Gmail&apos;s own accounting. Numbers above are actual confirmed sends logged after Gmail returned a message id.
          </p>
        </div>
      )}
    </div>
  );
}
